import { createContext, useContext, useEffect, useRef } from 'react';
import { IAgentQuoteData } from 'Feature/PageContent/Models/MyInsightsModel';
import { useLocalStorage } from 'Utilities/UseStorage';
import { useUserData } from 'Foundation/User/UserContext';
import * as defaultAgentQuoteData from 'Data/defaultAgentQuoteData.json';
import { IUserData } from './UserContextModels';

export type IQuoteDataContext = {
  agentQuoteData: IAgentQuoteData | null;
  getAgentQuoteData: (isReset: boolean) => void;
  updateAgentQuoteData: (newUserQuoteData: IAgentQuoteData) => void;
};

const initialState: IQuoteDataContext = {
  agentQuoteData: null,
  getAgentQuoteData: () => {},
  updateAgentQuoteData: () => {},
};

export const QuoteDataContext = createContext(initialState);
export const placeholderKey = 'AgentQuoteData_PLACEHOLDER';

export function craftStorageKey(userData: IUserData | undefined) {
  if (userData?.selectedStatCodeData?.SubStat) {
    return `${userData.isMockingAgent ? 'mocking_' : ''}AgentQuoteData_${userData.dpecid}`;
  } else {
    return placeholderKey;
  }
}

export function QuoteDataContextProvider({ children }: { children: React.ReactNode }) {
  const { userData } = useUserData();
  const expirationMinutes: number = 8 * 60;
  const localStorageKey = craftStorageKey(userData);
  const lastFetchAbortRef = useRef<AbortController | null>(null);
  const [userQuoteData, setUserQuoteData] = useLocalStorage<IAgentQuoteData>(
    localStorageKey,
    undefined,
    expirationMinutes,
  );

  // Equality guard (stringify fallback; replace with domain-specific comparison if needed)
  const isQuoteDataEqual = (a: IAgentQuoteData | null | undefined, b: IAgentQuoteData | null | undefined) => {
    if (a === b) return true;
    if (!a || !b) return false;
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  };

  const getAgentQuoteData = async (isReset: boolean) => {
    // Cancel any in-flight fetch to avoid race conditions overwriting fresher data
    if (lastFetchAbortRef.current) {
      lastFetchAbortRef.current.abort();
    }
    const abortController = new AbortController();
    lastFetchAbortRef.current = abortController;
    let nextData: IAgentQuoteData | null = null;
    try {
      const response = await fetch(`/api/sitecore/pla360/GetAgentQuoteData?isReset=${isReset}`, {
        signal: abortController.signal,
      });
      if (response.ok) {
        try {
          const data = await response.json();
          if (data) {
            nextData = data as IAgentQuoteData;
          } else {
            nextData = { UserID: userData?.dpecid } as IAgentQuoteData;
          }
        } catch (err) {
          nextData = { ...defaultAgentQuoteData, UserID: userData?.dpecid } as IAgentQuoteData;
        }
      } else {
        nextData = { ...defaultAgentQuoteData, UserID: userData?.dpecid } as IAgentQuoteData;
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        nextData = { ...defaultAgentQuoteData, UserID: userData?.dpecid } as IAgentQuoteData;
      } else {
        return; // aborted; do nothing
      }
    }

    if (nextData && !isQuoteDataEqual(userQuoteData, nextData)) {
      setUserQuoteData({ data: nextData });
    }
  };

  const updateAgentQuoteData = async (newUserQuoteData: IAgentQuoteData) => {
    try {
      await fetch('/api/sitecore/pla360/SetAgentQuoteData', {
        method: 'POST',
        body: new URLSearchParams({
          jsonData: JSON.stringify(newUserQuoteData),
        }),
      });
    } catch (err) {
      console.error('Error in UserQuoteContext.updateAgentQuoteData:', err);
    } finally {
      if (!isQuoteDataEqual(userQuoteData, newUserQuoteData)) {
        setUserQuoteData({ data: newUserQuoteData });
      }
    }
  };

  useEffect(() => {
    if (localStorageKey === placeholderKey || !localStorageKey.includes('mocking_')) {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('mocking_AgentQuoteData')) {
          localStorage.removeItem(key);
        }
      }
    }
  }, [localStorageKey]);

  // Fetch (or refetch) when:
  // - userData becomes available (initial load)
  // - cached quote data belongs to a different user
  useEffect(() => {
    if (userData?.isPL() && (!userQuoteData || userQuoteData.UserID !== userData?.dpecid)) {
      if (userData?.isMockingAgent) {
        getAgentQuoteData(true);
      } else {
        getAgentQuoteData(false);
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.dpecid, localStorageKey]);

  // Cross-tab synchronization: update state only when value meaningfully changes
  useEffect(() => {
    if (localStorageKey === placeholderKey) return;
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== localStorageKey || !e.newValue) return;
      try {
        const parsed = JSON.parse(e.newValue);
        const next = parsed?.data as IAgentQuoteData | undefined;
        if (next && !isQuoteDataEqual(userQuoteData, next)) {
          setUserQuoteData({ data: next });
        }
      } catch (err) {
        // Silently ignore malformed data
        console.warn('QuoteDataContext: Failed to parse storage event data', err);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStorageKey, userQuoteData]);

  let shared: IQuoteDataContext = {
    agentQuoteData: userQuoteData,
    getAgentQuoteData: getAgentQuoteData,
    updateAgentQuoteData: updateAgentQuoteData,
  };

  return !userQuoteData && userData?.isPL() ? (
    <></>
  ) : (
    <QuoteDataContext.Provider value={shared}>{children}</QuoteDataContext.Provider>
  );
}

export function useAgentQuoteData() {
  const context = useContext(QuoteDataContext);
  if (context === undefined) {
    throw new Error('useAgentQuoteData must be used within a QuoteDataContextProvider');
  }
  return context;
}
